import { useLayoutEffect, useRef } from "react";

/** Measure the existing controls, including padding, without changing their layout. */
function measureSelection(root: HTMLDivElement, snap: boolean) {
  const selected = root.querySelector<HTMLButtonElement>(
    "button[aria-pressed='true']",
  );
  const indicator = root.querySelector<HTMLElement>(
    ".denker-segmented-indicator",
  );
  if (
    !selected ||
    !indicator ||
    !selected.offsetWidth ||
    !selected.offsetHeight
  ) {
    delete root.dataset.indicatorReady;
    return;
  }
  const {
    offsetLeft: left,
    offsetTop: top,
    offsetWidth: width,
    offsetHeight: height,
  } = selected;
  const firstMeasurement = root.dataset.indicatorReady !== "true";
  if (snap || firstMeasurement) indicator.style.transition = "none";
  indicator.style.width = `${width}px`;
  indicator.style.height = `${height}px`;
  indicator.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  root.dataset.indicatorReady = "true";
  if (snap || firstMeasurement) {
    // Flush the snapped geometry before restoring transitions (also works in Safari 16).
    void indicator.offsetWidth;
    indicator.style.removeProperty("transition");
  }
}

export function useSlidingSelection(
  value: string,
  enabled: boolean,
  optionsKey: string,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const previousValue = useRef(value);

  useLayoutEffect(() => {
    if (enabled && rootRef.current) {
      measureSelection(rootRef.current, previousValue.current === value);
    }
    previousValue.current = value;
  }, [value, enabled, optionsKey]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root) return;
    const snapToSelection = () => measureSelection(root, true);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(snapToSelection);
    observer?.observe(root);
    for (const button of root.querySelectorAll("button"))
      observer?.observe(button);
    window.addEventListener("resize", snapToSelection);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", snapToSelection);
    };
  }, [enabled, optionsKey]);

  return rootRef;
}
